import { useEffect } from 'react';
import { SetterOrUpdater } from 'recoil';
import { useRecoilState, useRecoilValue } from 'recoil';
import { BundleCollectionHandle, isBundleCollectionHandle } from '../enums/CollectionHandle.enum';
import { Color } from '../enums/Color.enum';
import { Fit } from '../enums/Fit.enum';
import { PageContext } from '../enums/PageContext.enum';
import { ProductHandle } from '../enums/ProductHandle.enum';
import { SelectionType } from '../enums/SelectionType.enum';
import { SubContext } from '../enums/SubContext.enum';
import { DataLayerVariant } from '../models/DataLayer.model';
import { HoneyloveAbTests } from '../models/HoneyloveAbTests';
import { ViewVariant } from '../models/ShopifyVariant.model';
import { ViewProduct } from '../models/ViewProduct.model';
import { Denomination } from '../product_detail/components/denomination.enum';
import { LongSize } from '../product_detail/components/size.enum';
import {
  pageContextState,
  shopifyState,
  abTestState,
  _selectionColorFamily,
  _selectionDenominationFamily,
  _selectionFitFamily,
  _selectionHandleFamily,
  _selectionSizeFamily,
  _selectionSubContextFamily,
} from '../recoil/atoms';
import { variantLookup } from '../recoil/helpers/lookupHelpers';
import { getColor, getSize } from '../recoil/helpers/productHelper';
import { productsState } from '../recoil/selectors';
import {
  trackNavigationLinkClicked,
  trackProductColorChanged,
  trackProductDetailViewed,
  trackProductSizeChanged,
  trackProductStyleChanged,
} from '../tracking/events';
import { setLanderPath, setProductPath, setURLParam } from '../util';

interface ProductSelectionResponse {
  handle: ProductHandle;
  size: LongSize | undefined;
  fit: Fit | undefined;
  color: Color | undefined;
  variant: ViewVariant | undefined;
  product: ViewProduct;
  denomination: string | undefined;
  subContext: SubContext | undefined;
  setVariant: (variant: ViewVariant) => void;
  setColor: (color: Color | undefined) => void;
  setSize: (size: LongSize | undefined) => void;
  setFit: (fit: Fit) => void;
  setHandle: (handle: ProductHandle) => void;
  initializeHandle: SetterOrUpdater<ProductHandle>;
  initializeColor: SetterOrUpdater<Color | undefined>;
  initializeSize: SetterOrUpdater<LongSize | undefined>;
  initializeFit: SetterOrUpdater<Fit | undefined>;
  setDenomination: (denomination: Denomination) => void;
  setSubContext: (subContext: SubContext) => void;
}

export const useProductSelection = (
  selectionType: SelectionType,
  key = '0',
  initialProductHandle?: ProductHandle,
  initialColor?: Color,
): ProductSelectionResponse => {
  const shopify = useRecoilValue(shopifyState);
  const abTests = useRecoilValue(abTestState);
  const isProductSection = selectionType === SelectionType.ProductSection;
  const [subContext, setSubContext] = useRecoilState(_selectionSubContextFamily({ selectionType, key }));
  const pageContext = useRecoilValue(pageContextState);
  const products = useRecoilValue(productsState);
  const productFromHandle = (handle: ProductHandle) => handle && (products[handle] as ViewProduct);

  const [handle, _setHandle] = useRecoilState(_selectionHandleFamily({ selectionType, key }));
  const [size, _setSize] = useRecoilState(_selectionSizeFamily({ selectionType, key }));
  const [fit, _setFit] = useRecoilState(_selectionFitFamily({ selectionType, key }));
  const [denomination, setDenomination] = useRecoilState(_selectionDenominationFamily({ selectionType, key }));
  const [assignedColor, _setColor] = useRecoilState(_selectionColorFamily({ selectionType, key }));

  const { colors: unsortedColors, ...productProps } = productFromHandle(handle);
  let sortedColors = unsortedColors;
  for (const testKey in abTests) {
    const testIndex = [
      'AB2022-08-1',
      'AB2022-08-2',
      'AB2022-08-3',
      'AB2022-08-4',
      'AB2022-08-5',
      'AB2022-08-6',
      'AB2022-08-7',
      'AB2022-08-8',
      'AB2022-08-9',
      'AB2022-08-10',
    ].indexOf(testKey);
    if (abTests[testKey as keyof HoneyloveAbTests] !== handle) continue;
    if (!isProductSection || testIndex < 0 || testIndex > unsortedColors.length - 1) continue;
    sortedColors = [unsortedColors[testIndex], ...unsortedColors.filter((v, i) => i !== testIndex)];
    if (!assignedColor) {
      initialColor = sortedColors[0];
    }
  }
  const product = { colors: sortedColors, ...productProps };

  const color = getColor(product, assignedColor);
  const variant = variantLookup(product, color, size, fit, denomination);

  useEffect(() => {
    if (initialProductHandle) {
      _setHandle(initialProductHandle);
    }
  }, [_setHandle, initialProductHandle]);

  useEffect(() => {
    if (initialColor) {
      _setColor(initialColor);
    }
  }, [_setColor, initialColor]);

  const setColor = (updatedColor: Color | undefined) => {
    if (updatedColor) {
      isProductSection && setURLParam('color', updatedColor);
      const updatedVariant = variantLookup(product, updatedColor, size, fit, denomination);
      trackProductColorChanged(shopify, product, updatedVariant as DataLayerVariant, updatedColor, size);
    }
    _setColor(updatedColor);
  };

  const setSize = (updatedSize: LongSize | undefined) => {
    isProductSection && setURLParam('size', updatedSize);
    if (updatedSize && product) {
      const updatedVariant = variantLookup(product, color, updatedSize, fit, denomination);
      trackProductSizeChanged(shopify, product, updatedVariant as DataLayerVariant, color, updatedSize, fit);
    }
    _setSize(updatedSize);
  };

  const setFit = (updatedFit: Fit) => {
    isProductSection && setURLParam('fit', updatedFit);
    if (product && updatedFit !== fit) {
      const updatedVariant = variantLookup(product, color, size, updatedFit, denomination);
      trackProductSizeChanged(shopify, product, updatedVariant as DataLayerVariant, color, size, updatedFit);
    }
    _setFit(updatedFit);
  };

  const setHandle = (updatedHandle: ProductHandle) => {
    const updatedProduct = productFromHandle(updatedHandle);

    const updatedColor = getColor(updatedProduct, color);
    if (assignedColor === undefined || updatedColor !== color) {
      _setColor(updatedColor);
    }

    if (size) {
      const updatedSize = getSize(updatedProduct, size);
      if (updatedSize !== size) {
        _setSize(undefined);
      }
    }

    if (pageContext !== PageContext.LandingPage && selectionType === SelectionType.ProductSection) {
      setProductPath(updatedProduct, updatedColor, size);
    }
    if (subContext === SubContext.LanderProductToggle) {
      setLanderPath(product);
    }
    trackNavigationLinkClicked(product?.title, subContext);
    const updatedVariant = variantLookup(updatedProduct, updatedColor, size, fit, denomination);
    trackProductStyleChanged(shopify, updatedProduct, updatedVariant as DataLayerVariant, updatedColor, size);
    trackProductDetailViewed(shopify, updatedProduct, updatedVariant as DataLayerVariant, updatedColor, size);
    _setHandle(updatedHandle);
  };

  const setVariant = (updatedVariant: ViewVariant) => {
    if (!product.variants.includes(updatedVariant)) {
      throw new Error("Error calling setVariant with a variant that doesn't match the product");
    }
    setColor(updatedVariant.color);
    setSize(updatedVariant.size as LongSize);
    setFit(updatedVariant.fit ?? Fit.Regular);
    setDenomination(updatedVariant.denomination);
  };

  return {
    handle,
    product,
    size,
    fit,
    color,
    variant,
    denomination,
    subContext,
    setVariant,
    setColor,
    setSize,
    setFit,
    setHandle,
    initializeHandle: _setHandle,
    initializeColor: _setColor,
    initializeSize: _setSize,
    initializeFit: _setFit,
    setDenomination,
    setSubContext,
  };
};
